// @ts-check
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { messages } from '../shipping-grid/messages';
import { REFUND_ASSIST_STATUS_IDS } from '../shipping-grid/columns/void-type';
import { isNullIndicator } from '../../services/filtering/collection-filter-mapping-service';

/**
 * Void Type values
 * @typedef {object} VoidTypeValues
 * @property {string} MANUAL - Manually voided shipment
 * @property {string} REFUND_ASSIST - Voided via Refund Assist
 */
export const VOID_TYPE_VALUES = {
  MANUAL: 'manual',
  REFUND_ASSIST: 'refundAssist',
};

export const VOID_TYPE_FILTER_VALUES = {
  [VOID_TYPE_VALUES.MANUAL]: isNullIndicator,
  [VOID_TYPE_VALUES.REFUND_ASSIST]: REFUND_ASSIST_STATUS_IDS,
};

/**
 * Message descriptors for void types
 * Maps void type values to their corresponding message descriptors
 */
const VOID_TYPE_MESSAGES = {
  [VOID_TYPE_VALUES.MANUAL]: messages.voidTypeManual,
  [VOID_TYPE_VALUES.REFUND_ASSIST]: messages.voidTypeRefundAssist,
};

/**
 * Returns a formatted message component for the given void type
 * @param {string} voidType - The void type value ('manual' or 'refundAssist')
 * @returns {React.ReactElement|string} FormattedMessage component or the raw value if unknown
 */
export const getVoidTypeMessage = (voidType) => {
  const messageDescriptor = VOID_TYPE_MESSAGES[voidType];

  if (!messageDescriptor) {
    return String(voidType);
  }

  return <FormattedMessage {...messageDescriptor} />;
};

/**
 * Returns the message descriptor for the given void type
 * Useful when you need just the descriptor without the React component
 * @param {string} voidType - The void type value ('manual' or 'refundAssist')
 * @returns {object | null} Message descriptor object or null if unknown
 */
export const getVoidTypeMessageDescriptor = (voidType) => {
  return VOID_TYPE_MESSAGES[voidType] || null;
};

/**
 * Checks if a void type value is valid
 * @param {string} voidType - The void type value to validate
 * @returns {boolean} True if the void type value is valid
 */
export const isValidVoidType = (voidType) => {
  return Object.values(VOID_TYPE_VALUES).includes(voidType);
};

/**
 * Maps filter criterion values back to void type keys for display purposes
 * This is needed because the API receives different values than what we use for display:
 * - Manual: API gets isNull operator, display needs 'manual' key
 * - Refund Assist: API gets [3,4,6] values, display needs 'refundAssist' key
 * @param {string | number[] | any} criterionValue - The criterion value from the filter
 * @param {string} criterionOperator - The criterion operator from the filter
 * @returns {string | null} The void type key ('manual' or 'refundAssist') or null if not found
 */
export const getVoidTypeKeyFromFilterValue = (criterionValue, criterionOperator) => {
  // Check for Manual void type (handles both raw and transformed formats):
  // - Raw format (before transformation): criterionValue === 'empty'
  // - Transformed format (after transformation): criterionOperator === 'empty'
  if (criterionOperator === isNullIndicator || criterionValue === isNullIndicator) {
    return VOID_TYPE_VALUES.MANUAL;
  }

  // If criterionValue is the refund assist status IDs array, it's Refund Assist
  if (
    Array.isArray(criterionValue) &&
    criterionValue.length === REFUND_ASSIST_STATUS_IDS.length &&
    criterionValue.every((id) => REFUND_ASSIST_STATUS_IDS.includes(id))
  ) {
    return VOID_TYPE_VALUES.REFUND_ASSIST;
  }

  return null;
};

/**
 * Returns criteria information for void type filter
 * Each criterion has a value (the void type string or array of status IDs) and displayName (formatted message)
 * @returns {Array<{value: string | number[], displayName: React.ReactElement}>} Array of criteria for filter options
 */
export const getVoidTypeFilterCriteria = () => {
  const voidTypes = [VOID_TYPE_VALUES.MANUAL, VOID_TYPE_VALUES.REFUND_ASSIST];

  return voidTypes.map((voidType) => {
    const messageDescriptor = VOID_TYPE_MESSAGES[voidType];
    return {
      value: VOID_TYPE_FILTER_VALUES[voidType],
      displayName: messageDescriptor ? <FormattedMessage {...messageDescriptor} /> : <span>{voidType}</span>,
    };
  });
};
